import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import useMobile from "../../hooks/useMobile";
import { ICooperativeDetails } from "../../exports/interfaces";
import CooperativeInfo from "./CooperativeInfo";
import AddressInfo from "./AddressInfo";
import FinancialInfo from "./FinancialInfo";
import CommitteesInfo from "./CommitteesInfo";
import MemberInfo from "./MemberInfo";
import DocumentsInfo from "./DocumentsInfo";
import { UserRoundPlus } from "lucide-react";
import { useOption } from "../../context/OptionContext";
import { darkenColor } from "../../exports/functions";

const natureClass = (nature: string) => {
  switch (nature) {
    case "Multipurpose":
      return "pill-info";
    case "Saving and Credit":
      return "pill-danger";
    case "Agricultural":
      return "pill-success";
    case "Others":
      return "pill-primary";
    default:
      return "";
  }
};

const CooperativeDetails = ({
  cooperative,
}: {
  cooperative: ICooperativeDetails | null;
}) => {
  const { id } = useParams();
  const { statusOptions } = useOption();

  const data = {
    id,
    name: "Cooperatives Name",
    type: "Saving and Credits",
    status: "Active",
    email: "cooperative@gmail.com",
    phone: "+977 980123456",
    pan: "1234567890",
    address: "Municipality, District, Province, Nepal",
  };

  const isMobile = useMobile();
  const navigate = useNavigate();

  const statusStyle = (status: string) => {
    const cooperativeStatuses =
      statusOptions?.find((item) => item.name === "Cooperative Statuses")
        ?.statusOptions || [];

    const matchedStatus = cooperativeStatuses.find(
      (item) => item.label === status,
    );

    return {
      backgroundColor: matchedStatus?.color || "#ccc",
      color: darkenColor(matchedStatus?.color || "#ccc", 40),
    };
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-3">Cooperatives Details</h3>
        </div>

        <button
          className="btn px-3 btn-badge"
          onClick={() => navigate("/register-new")}
        >
          <i className={`${!isMobile ? "me-2" : ""}`}>
            <UserRoundPlus size={20} />
          </i>
          {!isMobile && "Register Here"}
        </button>
      </div>

      <div className="dashboard-card p-3">
        {/* Top Section */}
        <div className="d-flex justify-content-between align-items-start">
          <div className="d-flex gap-3 align-items-center flex-wrap">
            <div
              style={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                background: "#ddd",
              }}
            />

            {!isMobile && (
              <div>
                <h4 className="mb-1">
                  {cooperative?.nameEnglish}
                  <span
                    className={`badge ${natureClass(cooperative?.natureOfWork || "-")} ms-2`}
                  >
                    {cooperative?.natureOfWork}
                  </span>
                  <span
                    className="badge bg-success-subtle pill-success ms-2"
                    style={statusStyle(data.status)}
                  >
                    {data.status}
                  </span>
                </h4>

                <small>{cooperative?.mobileNumber}</small>
                <br />
                <small className="text-primary">{cooperative?.email}</small>
                <br />
                <small>
                  {cooperative?.address?.municipality},{" "}
                  {cooperative?.address?.district},{" "}
                  {cooperative?.address?.province}
                </small>
              </div>
            )}
          </div>

          {/* {!isMobile && (
            <button className="btn px-3 btn-badge fs-small">
              <i className={`fa-solid fa-pen ${!isMobile ? "me-2" : ""}`}></i>
              {!isMobile && "Edit"}
            </button>
          )} */}
          {isMobile && (
            <div className="text-end">
              {/* <button className="btn px-3 btn-badge fs-small">
                <i className={`fa-solid fa-pen ${!isMobile ? "me-2" : ""}`}></i>
                {!isMobile && "Edit"}
              </button> */}
              <h4 className="mb-1">
                {cooperative?.nameEnglish}
                <span
                  className={`badge ${natureClass(cooperative?.natureOfWork || "-")} ms-2 mb-2`}
                >
                  {cooperative?.natureOfWork}
                </span>
                <span className="badge bg-success-subtle pill-success ms-2">
                  {data.status}
                </span>
              </h4>

              <small>{cooperative?.mobileNumber}</small>
              <br />
              <small className="text-primary">{cooperative?.email}</small>
              <br />
              <small>
                {cooperative?.address?.municipality},{" "}
                {cooperative?.address?.district},{" "}
                {cooperative?.address?.province}
              </small>
            </div>
          )}
        </div>

        <hr />

        <CooperativeInfo cooperative={cooperative} />

        <AddressInfo cooperative={cooperative} />

        <FinancialInfo cooperative={cooperative} />

        <CommitteesInfo cooperative={cooperative} />

        <MemberInfo cooperative={cooperative} />

        <DocumentsInfo cooperative={cooperative} />
      </div>
    </>
  );
};

export const Info = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => (
  <div className="col-md-4 details-text">
    <small className="d-block">{label}</small>
    <p>{value}</p>
  </div>
);

export default CooperativeDetails;
